/**
 * Projection Service
 * Handles API calls for spot price projection (YoY) data
 */

import { api } from './client.js';

export interface BarraOption {
  value: string;
  label: string;
}

export interface ProjectionStatus {
  hasProjection: boolean;
  selectedBarra: string | null;
  projectionInfo?: {
    name: string;
    dateRange: {
      startYear: number;
      startMonth: number;
      endYear: number;
      endMonth: number;
    };
    availableBarras: string[];
  };
}

export interface YearlyProjection {
  year: number;
  avgPrice: number;
  monthlyPrices: Record<string, number>;
}

export interface MonthlyPriceData {
  year: number;
  month: number;
  monthName: string;
  date: string;
  price: number;
}

export interface BarraProjectionData {
  success: boolean;
  barra: string;
  years: number[];
  monthlyData: MonthlyPriceData[];
  yearlyAverages: Record<number, number>;
  statistics: {
    minPrice: number;
    maxPrice: number;
    avgPrice: number;
    totalMonths: number;
    startYear: number;
    endYear: number;
    trendPercent?: number;
  };
  dateRange: {
    startYear: number;
    startMonth: number;
    endYear: number;
    endMonth: number;
  };
}

export interface ProjectionSummary {
  barra: string;
  years: number[];
  yearlyAverages: Record<number, number>;
  overallAvg: number;
  trendPercent: number;
}

export interface ProjectedFinancials {
  useProjection: boolean;
  selectedBarra: string;
  yearlySavings: number[];
  avgAnnualSavings: number;
  npv: number;
  irr: number;
  paybackYears: number;
  comparison: {
    npvDiff: number;
    irrDiff: number;
    paybackDiff: number;
  };
}

export interface FinancialImpactPreview {
  constant: {
    npv: number;
    irr: number;
    paybackYears: number;
    annualSavings: number;
  };
  projected: {
    npv: number;
    irr: number;
    paybackYears: number;
    yearlySavings: number[];
    avgAnnualSavings: number;
  };
  comparison: {
    npvDiff: number;
    npvDiffPercent: number;
    irrDiff: number;
    paybackDiff: number;
  };
  barra: string;
}

class ProjectionService {
  private readonly basePath = '/projection';

  /**
   * Get available barras for the dropdown
   */
  async getBarras(): Promise<{ barras: BarraOption[]; hasProjection: boolean }> {
    return api.get(`${this.basePath}/barras`);
  }

  /**
   * Get project's current projection status
   */
  async getProjectStatus(projectId: string): Promise<ProjectionStatus> {
    return api.get(`${this.basePath}/project-status`, {
      params: { project_id: projectId },
    });
  }

  /**
   * Select or clear a barra for a project.
   * Pass null to clear the selection.
   */
  async selectBarra(projectId: string, barra: string | null): Promise<{ success: boolean; message: string }> {
    return api.post(`${this.basePath}/select-barra`, {
      project_id: projectId,
      barra,
    });
  }

  /**
   * Preview financial impact of projection
   */
  async previewImpact(projectId: string): Promise<FinancialImpactPreview | null> {
    return api.get(`${this.basePath}/preview-impact`, {
      params: { project_id: projectId },
    });
  }

  /**
   * Get projection data for a specific barra
   */
  async getBarraProjection(barra: string): Promise<BarraProjectionData> {
    return api.get(`${this.basePath}/barra-data`, {
      params: { barra },
    });
  }

  /**
   * Get projection data by project ID (uses project's selected barra)
   */
  async getProjectBarraProjection(projectId: string): Promise<BarraProjectionData> {
    return api.get(`${this.basePath}/barra-data`, {
      params: { project_id: projectId },
    });
  }
}

export const projectionService = new ProjectionService();
